import { useState } from 'react'
import { ColumnDef } from '@tanstack/react-table'
import { Plus, Sparkles, CheckCircle2, XCircle, Play, Clock } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { Link } from 'react-router-dom'
import { formatDistanceToNow } from 'date-fns'
import { useGenClairRuns, useBulkDeleteGenClairRuns, GenClairRun } from '@/hooks/useGenClairRuns'
import { DataTable } from '@/components/common/DataTable'
import { useToast } from '@/hooks/use-toast'

const statusBadge = (status: GenClairRun['status']) => {
  switch (status) {
    case 'completed':
      return <Badge variant="default" className="bg-green-600"><CheckCircle2 className="h-3 w-3 mr-1" />Completed</Badge>
    case 'running':
      return <Badge variant="secondary" className="animate-pulse"><Play className="h-3 w-3 mr-1" />Running</Badge>
    case 'pending':
      return <Badge variant="outline"><Clock className="h-3 w-3 mr-1" />Pending</Badge>
    case 'failed':
      return <Badge variant="destructive"><XCircle className="h-3 w-3 mr-1" />Failed</Badge>
    default:
      return <Badge variant="outline">{status}</Badge>
  }
}

const sourceLabel = (sourceType: GenClairRun['source_type']) => {
  return sourceType === 'ragflow_dataset' ? 'Knowledge Base' : 'File Upload'
}

const columns: ColumnDef<GenClairRun, any>[] = [
  {
    accessorKey: 'name',
    header: 'Name',
    cell: ({ row }) => (
      <Link
        to={`/app/genclair/${row.original.id}`}
        className="font-medium text-primary hover:underline"
      >
        {row.original.name || 'Untitled Run'}
      </Link>
    ),
  },
  {
    accessorKey: 'description',
    header: 'Description',
    cell: ({ row }) => (
      <span className="text-muted-foreground line-clamp-1 max-w-[250px]">
        {row.original.description || '—'}
      </span>
    ),
  },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => statusBadge(row.original.status),
  },
  {
    accessorKey: 'source_type',
    header: 'Source',
    cell: ({ row }) => sourceLabel(row.original.source_type),
  },
  {
    accessorKey: 'intents_discovered',
    header: 'Intents',
    cell: ({ row }) => row.original.intents_discovered,
  },
  {
    accessorKey: 'created_at',
    header: 'Created',
    cell: ({ row }) =>
      formatDistanceToNow(new Date(row.original.created_at), { addSuffix: true }),
  },
]

export function GenClairList() {
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [search, setSearch] = useState('')

  const { data, isLoading } = useGenClairRuns({ page, pageSize, search })
  const bulkDelete = useBulkDeleteGenClairRuns()
  const { toast } = useToast()

  const handleBulkDelete = (ids: string[]) => {
    bulkDelete.mutate(ids, {
      onSuccess: (res) => {
        toast({ title: `Deleted ${res.deleted_count} run(s)` })
      },
      onError: () => {
        toast({ title: 'Failed to delete', variant: 'destructive' })
      },
    })
  }

  const emptyState = (
    <Card className="border-dashed border-0 shadow-none">
      <CardContent className="flex flex-col items-center justify-center py-16">
        <Sparkles className="h-12 w-12 text-muted-foreground mb-4" />
        <h3 className="text-lg font-semibold mb-2">No Taxonomy Runs Yet</h3>
        <p className="text-muted-foreground text-center max-w-md mb-6">
          GenClair discovers taxonomy, intents, tags, and entity types from your data.
          Start by uploading a file or running discovery on a knowledge base.
        </p>
        <Button asChild>
          <Link to="/app/genclair/create">
            <Plus className="h-4 w-4 mr-2" />
            Start Your First Run
          </Link>
        </Button>
      </CardContent>
    </Card>
  )

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Sparkles className="h-8 w-8 text-purple-500" />
            GenClair
          </h1>
          <p className="text-muted-foreground mt-2">
            Discover taxonomy, intents, and entity types from your data
          </p>
        </div>
        <Button asChild>
          <Link to="/app/genclair/create">
            <Plus className="h-4 w-4 mr-2" />
            New Run
          </Link>
        </Button>
      </div>

      <DataTable
        columns={columns}
        data={data?.items ?? []}
        total={data?.total ?? 0}
        page={page}
        pageSize={pageSize}
        search={search}
        isLoading={isLoading}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        onSearchChange={setSearch}
        onBulkDelete={handleBulkDelete}
        isBulkDeleting={bulkDelete.isPending}
        getRowId={(row) => row.id}
        emptyState={emptyState}
      />
    </div>
  )
}
